import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
const API_URL = process.env.REACT_APP_API_URL;


export default function Home() {
  const [search, setSearch] = useState("");
  const [teachers, setTeachers] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [assistantOpen, setAssistantOpen] = useState(false);

  const fetchTeachers = useCallback(async (reset = false) => {
    try {
      setLoading(true);
      const res = await fetch(
        `${API_URL}/api/teacher/teachers/featured?search=${encodeURIComponent(
          search
        )}&page=${page}&limit=6`
      );
      const data = await res.json();
      if (!Array.isArray(data)) throw new Error("Invalid response");

      if (reset) {
        setTeachers(data);
      } else {
        setTeachers((prev) => [...prev, ...data]);
      }

      setHasMore(data.length >= 6);
      setLoading(false);
    } catch (err) {
      console.error("Error:", err);
      setError("❌ Failed to load teachers.");
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    fetchTeachers(page === 1);
  }, [page, fetchTeachers]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    setPage(1);
    setTeachers([]);
  };

  return (
    <main className="font-sans min-h-screen flex flex-col bg-white text-gray-800">
      <header className="text-white text-center py-20 px-4 bg-gradient-to-r from-blue-600 to-purple-700">
        <h1 className="text-4xl font-bold mb-4">Learn From The Best Teachers</h1>
        <p className="text-lg mb-6">Choose your guide, set your pace, shape your journey.</p>
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <a href="/teachers" className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition">Browse Teachers</a>
          <a href="/login" className="bg-white text-purple-700 px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition">Login / Register</a>
        </div>
      </header>

      <section className="bg-gray-50 py-12 text-center px-4">
        <input
          type="text"
          placeholder="Search teachers by name or subject..."
          className="border border-gray-300 rounded-lg px-4 py-3 w-full max-w-md mx-auto focus:ring focus:outline-none"
          value={search}
          onChange={handleSearchChange}
        />
      </section>

      <section className="py-16 px-4 sm:px-6 md:px-8 lg:px-16 xl:px-24">
        <h2 className="text-center text-3xl font-bold text-gray-800 mb-10 drop-shadow-sm">Featured Teachers</h2>

        {error && <p className="text-center text-red-600 mb-4 font-medium">{error}</p>}

        {loading && page === 1 ? (
          <p className="text-center text-gray-500 text-lg">Loading...</p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {teachers.length === 0 ? (
                <p className="text-center text-gray-500 col-span-full text-lg">No teachers found.</p>
              ) : (
                teachers.map((teacher) => (
                  <div className="shadow-lg rounded-xl transition transform hover:scale-[1.01] hover:shadow-xl" key={teacher._id || teacher.name}>
                    <TeacherCard teacher={teacher} />
                  </div>
                ))
              )}
            </div>

            {hasMore && (
              <div className="mt-12 text-center">
                <button
                  onClick={() => setPage((prev) => prev + 1)}
                  className="bg-blue-600 text-white px-8 py-3 rounded-xl shadow-md hover:bg-blue-700 hover:shadow-lg transition-all duration-200 ease-in-out disabled:opacity-60"
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Show More"}
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <section className="bg-gray-50 py-16 px-4">
        <h2 className="text-center text-2xl font-semibold mb-10">How It Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Step number={1} title="Find Your Teacher" desc="Search by subject, rating, or price." />
          <Step number={2} title="Learn & Chat" desc="Join sessions or learn with shared notes." />
          <Step number={3} title="Rate & Review" desc="Help others by sharing your experience." />
        </div>
      </section>

      <footer className="text-center py-6 text-gray-300 bg-gray-900">
        <p>© 2025 E-School Platform. All rights reserved.</p>
        <div className="mt-2 space-x-4">
          <a href="/about" className="hover:underline">About</a>
          <a href="/contact" className="hover:underline">Contact</a>
          <a href="/terms" className="hover:underline">Terms</a>
        </div>
      </footer>

      {/* AI Assistant Widget */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setAssistantOpen(!assistantOpen)}
          className="bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 focus:outline-none transition duration-300 text-xl"
          aria-label="Toggle Assistant"
        >
          {assistantOpen ? "×" : "🤖"}
        </button>

        {assistantOpen && (
          <div className="mt-4 w-80 max-w-xs bg-white rounded-xl shadow-2xl p-4 text-sm text-gray-800 space-y-3 animate-fade-in">
            <h3 className="font-bold text-lg">Hi! I'm your AI Assistant 👋</h3>
            <ul className="space-y-2 list-disc list-inside">
              <li>Need help <strong>registering</strong>?</li>
              <li>Want to <strong>pay</strong> for a course?</li>
              <li>Join a <strong>live session</strong></li>
              <li>Download <strong>course materials</strong></li>
              <li>Check your <strong>schedule</strong></li>
            </ul>
            <p className="text-blue-600 font-medium">Let’s learn smarter together!</p>
          </div>
        )}
      </div>
    </main>
  );
}

function TeacherCard({ teacher }) {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/profile/${teacher._id}`);
  };

  const initials = teacher?.name
    ? teacher.name
        .split(" ")
        .map((n) => n[0])
        .join("")
    : "NA";

  return (
    <div
      onClick={handleClick}
      className="border rounded-lg p-5 shadow-md bg-white hover:shadow-lg transition cursor-pointer"
    >
      <div className="flex justify-center mb-4">
        {teacher.profilePhoto ? (
          <img
            src={`${API_URL}/uploads/${teacher.profilePhoto}`}
            alt={teacher.name || "Unnamed"}
            className="w-20 h-20 object-cover rounded-full shadow"
          />
        ) : (
          <div className="w-20 h-20 flex items-center justify-center bg-gray-200 rounded-full text-gray-600 font-bold">
            {initials}
          </div>
        )}
      </div>
      <h3 className="text-lg font-bold text-center mb-1">
        {teacher.name || "Unknown Teacher"}
      </h3>
      <p className="text-sm text-gray-600 text-center mb-2">
        {teacher.subjects_taught || "Subject not listed"}
      </p>
      <p className="text-sm text-gray-700 mb-3 line-clamp-3">
        {teacher.description || "No description available."}
      </p>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-blue-600">
          ${teacher.hourly_rate || 0}/hr
        </span>
        <div className="flex items-center gap-1">
          <StarRating rating={teacher.rating || 0} />
          <span className="text-sm text-gray-700">
            {teacher.rating?.toFixed(1) || "N/A"}
          </span>
        </div>
      </div>
    </div>
  );
}

function StarRating({ rating }) {
  const fullStars = Math.floor(rating);
  return (
    <div className="flex text-yellow-400">
      {[...Array(fullStars)].map((_, i) => (
        <span key={i}>★</span>
      ))}
    </div>
  );
}

function Step({ number, title, desc }) {
  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="text-3xl font-bold text-blue-600 mb-2">{number}</div>
      <h3 className="text-lg font-semibold mb-1">{title}</h3>
      <p className="text-gray-600">{desc}</p>
    </div>
  );
}
